<script>
  import { navigate } from 'svelte-routing';
  import { getApiUrl } from '../api';
  import { cloneStore } from '../cloneStore.js';
  import { onMount, onDestroy } from 'svelte';
  import { Html5QrcodeScanner } from "html5-qrcode";
  import { authFetch } from '../authService'; // <-- IMPORT THIS

  let isLoading = false;
  let error = '';
  let fileInput;
  let barcode = '';
  
  // Camera Scanning State
  let isScanning = false;
  let scanner = null;

  async function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    isLoading = true;
    error = '';
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = async () => {
      const base64String = reader.result.split(',')[1];
      try {
        const response = await authFetch(getApiUrl('/products/scan-image'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ imageBase64: base64String }),
        });
        if (!response.ok) {
            const errData = await response.json();
            throw new Error(errData.error || 'Failed to scan image.');
        }
        const data = await response.json();
        
        cloneStore.set({
          ...data,
          imageBase64: base64String 
        });
        
        navigate('/products/add');
      } catch (err) {
        error = err.message;
      } finally {
        isLoading = false;
      }
    };
    reader.onerror = () => {
      error = 'Failed to read image file.';
      isLoading = false;
    };
  }

  function handleBarcodeSubmit() {
    if (!barcode) return;
    cloneStore.set({ barcode: barcode });
    navigate('/products/add');
  }

  // --- CAMERA SCANNING LOGIC ---
  function startCameraScan() {
    isScanning = true;
    error = '';
    // Wait for the DOM to update and show the "reader" div
    setTimeout(() => {
        scanner = new Html5QrcodeScanner(
            "reader", 
            { 
                fps: 10, 
                qrbox: { width: 250, height: 250 },
                aspectRatio: 1.0
            },
            /* verbose= */ false
        );
        scanner.render(onScanSuccess, onScanFailure);
    }, 100);
  }

  function onScanSuccess(decodedText, decodedResult) {
    console.log(`Code matched = ${decodedText}`, decodedResult);
    barcode = decodedText; // Fill the input
    stopCameraScan(); // Stop scanning immediately
  }

  function onScanFailure(error) {
    // This fires frequently while scanning, usually ignored to prevent console spam
    // console.warn(`Code scan error = ${error}`);
  }

  function stopCameraScan() {
    if (scanner) {
        scanner.clear().then(() => {
            isScanning = false;
            scanner = null;
        }).catch((err) => {
            console.error("Failed to clear scanner. ", err);
        });
    } else {
        isScanning = false;
    }
  }

  // Cleanup on page leave
  onDestroy(() => {
      if(scanner) {
          scanner.clear().catch(error => console.error("Failed to clear scanner", error));
      }
  });
</script>

<div class="scan-container">
  <h2>Add a Product Discount</h2>
  <p>You can either scan a photo of the product tag or enter its barcode number.</p>

  <div class="scan-option">
    <h3>Option 1: Scan Photo (Gemini AI)</h3>
    <p class="hint">Upload a photo of the price tag or promotional poster.</p>
    <input
      type="file"
      accept="image/png, image/jpeg"
      on:change={handleFileChange}
      bind:this={fileInput}
      style="display: none;"
      id="file-uploader"
    />
    <button class="upload-btn" on:click={() => fileInput.click()} disabled={isLoading}>
      {#if isLoading}Scanning...{:else}Upload Image{/if}
    </button>
  </div>

  <div class="scan-option">
    <h3>Option 2: Barcode</h3>
    <p class="hint">Scan a barcode with your camera or enter it manually.</p>
    
    <!-- Camera Viewfinder Area -->
    {#if isScanning}
        <div id="reader" class="camera-box"></div>
        <button class="stop-btn" on:click={stopCameraScan}>Stop Camera</button>
    {:else}
        <button class="camera-btn" on:click={startCameraScan}>
            📷 Open Camera
        </button>
    {/if}

    <div class="divider">OR</div>

    <form on:submit|preventDefault={handleBarcodeSubmit} class="barcode-form">
      <input type="text" bind:value={barcode} placeholder="Enter barcode number" />
      <button type="submit">Go</button>
    </form>
  </div>

  {#if error}
    <p class="error">{error}</p>
  {/if}
</div>

<style>
  .scan-container { max-width: 700px; margin: 2rem auto; padding: 0 1rem; }
  
  .scan-option {
    background: #fff; padding: 2rem; border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0,0,0,0.05); text-align: center;
    margin-bottom: 2rem;
    border: 1px solid #dee2e6;
  }
  
  h3 { margin-top: 0; color: #0056b3; }
  .hint { color: #6c757d; font-size: 0.9rem; margin-bottom: 1.5rem; }

  .upload-btn {
    padding: 1rem 2rem; font-size: 1.1rem; background-color: #007bff;
    color: white; border: none; border-radius: 8px; cursor: pointer;
    transition: background-color 0.2s;
  }
  .upload-btn:hover { background-color: #0056b3; }
  .upload-btn:disabled { background-color: #aaa; cursor: not-allowed; }
  
  /* Camera Styles */
  .camera-btn {
      padding: 0.8rem 1.5rem; background-color: #17a2b8; color: white;
      border: none; border-radius: 4px; font-size: 1rem; cursor: pointer;
      margin-bottom: 1rem;
  }
  .stop-btn {
      background-color: #dc3545; color: white; border: none; padding: 0.5rem 1rem;
      border-radius: 4px; margin-top: 10px; cursor: pointer;
  }
  .camera-box {
      width: 100%;
      max-width: 400px;
      margin: 0 auto;
      border: 2px solid #333;
      border-radius: 4px;
  }
  
  .divider {
      margin: 1.5rem 0;
      font-weight: bold;
      color: #ccc;
      display: flex;
      align-items: center;
      justify-content: center;
  }
  .divider::before, .divider::after {
      content: ""; flex: 1; border-bottom: 1px solid #eee; margin: 0 10px;
  }

  .barcode-form { display: flex; gap: 1rem; justify-content: center; }
  .barcode-form input { 
      flex-grow: 1; max-width: 300px; padding: 0.75rem; 
      font-size: 1rem; border: 1px solid #ccc; border-radius: 4px; 
  }
  .barcode-form button { 
      padding: 0.75rem 1.5rem; background-color: #28a745; 
      color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 1rem;
  }
  
  .error { 
      color: #dc3545; margin-top: 1rem; font-weight: bold; 
      text-align: center; padding: 1rem; background-color: #fff5f5; border-radius: 4px;
  }
</style>